import React from 'react';
import { Page, Text, View } from '@react-pdf/renderer';
import { formatDate, styles } from './pdfShared';
import { ReportPdfLogo } from './ReportPdfLogo';
import { formatCurrency, formatDecimal } from '../../../utils/format';
import type { CustomerPurchaseDetailResponse, CustomerReportRow } from '../../../services/report.service';
import type { TFn } from '../export/context';

interface Props {
  customers: ReadonlyArray<CustomerReportRow>;
  details: Map<string, CustomerPurchaseDetailResponse>;
  t: TFn;
  currencySymbol: string;
}

export const CustomerDetailPages: React.FC<Props> = ({ customers, details, t, currencySymbol }) => (
  <>
    {customers.map((c) => {
      const detail = details.get(c.customerId);
      if (!detail) return null;
      const fgs = [...detail.finishedGoods].sort((a, b) => b.totalPcs - a.totalPcs || a.name.localeCompare(b.name));
      const grand = fgs.reduce((sum, fg) => sum + fg.totalPcs, 0);
      return (
        <Page key={c.customerId} size="A4" style={styles.page}>
          <ReportPdfLogo />
          <Text style={styles.title}>{c.customerName}</Text>
          <Text style={styles.subtitle}>{c.customerCode}</Text>
          <View style={styles.summaryGrid}>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>{t('reports.customer.summary.totalPcs')}</Text>
              <Text style={styles.summaryValue}>{formatDecimal(detail.summary.totalPcs, 0)}</Text>
            </View>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>{t('reports.customer.summary.totalOrders')}</Text>
              <Text style={styles.summaryValue}>{formatDecimal(detail.summary.totalOrders, 0)}</Text>
            </View>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>{t('reports.customer.summary.totalRevenue')}</Text>
              <Text style={styles.summaryValue}>{formatCurrency(detail.summary.totalRevenue, currencySymbol)}</Text>
            </View>
            <View style={styles.summaryCard}>
              <Text style={styles.summaryLabel}>{t('reports.customer.summary.topFinishedGood')}</Text>
              <Text style={styles.summaryValue}>
                {detail.summary.topFinishedGood
                  ? `${detail.summary.topFinishedGood.name} (${formatDecimal(detail.summary.topFinishedGood.pcs, 0)})`
                  : t('reports.customer.summary.none')}
              </Text>
            </View>
          </View>
          {fgs.map((fg) => (
            <View key={fg.itemId} wrap={false} style={{ marginBottom: 8 }}>
              <Text style={[styles.headText, { fontSize: 10, marginBottom: 2 }]}>{fg.name}</Text>
              <View style={styles.table}>
                <View style={[styles.row, styles.headerRow]}>
                  <Text style={[styles.cell, styles.headText, { flexGrow: 35 }]}>{t('reports.customer.history.date')}</Text>
                  <Text style={[styles.cell, styles.headText, { flexGrow: 40 }]}>{t('reports.customer.history.invoice')}</Text>
                  <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 25 }]}>{t('reports.customer.history.qty')}</Text>
                </View>
                {fg.purchases.map((p, i) => (
                  <View key={i} style={styles.row}>
                    <Text style={[styles.cell, { flexGrow: 35 }]}>{formatDate(p.date)}</Text>
                    <Text style={[styles.cell, { flexGrow: 40 }]}>{p.orderNumber}</Text>
                    <Text style={[styles.cell, styles.right, { flexGrow: 25 }]}>{formatDecimal(p.qty, 0)}</Text>
                  </View>
                ))}
                <View style={styles.row}>
                  <Text style={[styles.cell, { flexGrow: 35 }]}> </Text>
                  <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 40 }]}>{t('reports.export.subtotal')}</Text>
                  <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 25 }]}>{formatDecimal(fg.totalPcs, 0)}</Text>
                </View>
              </View>
            </View>
          ))}
          <View style={[styles.row, styles.headerRow]}>
            <Text style={[styles.cell, { flexGrow: 35 }]}> </Text>
            <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 40 }]}>{t('reports.export.grandTotal')}</Text>
            <Text style={[styles.cell, styles.headText, styles.right, { flexGrow: 25 }]}>{formatDecimal(grand, 0)}</Text>
          </View>
        </Page>
      );
    })}
  </>
);
